import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Grid, Box } from '@mui/material';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import axios from 'axios';
import 'tailwindcss/tailwind.css';

// Register Chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const Reconciliation_Dashboard = () => {
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const projectId = localStorage.getItem('selectedProject');
        if (!projectId) {
          throw new Error('No project selected');
        }

        const response = await axios.post(`${baseURL}dashboard_metrics`, {
          projectId: projectId
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const metrics = response.data.data.metrics;

        // Transform API data to match the dashboard format
        const transformedData = [
          { title: 'Total Planned Modules', value: metrics.total_modules, color: '#3B82F6' },
          { title: 'Total Modules Accepted from Receiving', value: metrics.total_accepted_modules, color: '#10B981' },
          { title: 'Modules Damaged in Internal Transit', value: metrics.damaged_modules.transit, color: '#EF4444' },
          { title: 'Modules Damaged During Installation', value: metrics.damaged_modules.installation, color: '#F59E0B' },
          { title: 'Modules Damaged During Unloading', value: metrics.damaged_modules.unloading, color: '#8B5CF6' },
          { title: 'Modules Installed on Tables', value: metrics.installed_modules, color: '#EC4899' },
          { title: 'Modules in Store', value: metrics.store_modules, color: '#6B7280' },
          { title: 'Modules Theft', value: metrics.theft_modules, color: '#D1D5DB' },
          { title: 'Defective Modules', value: metrics.damaged_modules.defective, color: '#F87171' }, 
          { title: 'Unaccounted Modules', value: metrics.unaccounted_modules, color: '#60A5FA' } 
        ];

        setDashboardData(transformedData);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Data for the bar chart
  const chartData = {
    labels: dashboardData.map(item => item.title),
    datasets: [
      {
        label: 'Module Counts',
        data: dashboardData.map(item => item.value),
        backgroundColor: dashboardData.map(item => item.color),
        borderColor: dashboardData.map(item => item.color),
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 14,
            family: 'Inter, sans-serif',
          },
          color: '#ffffff',
        },
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleFont: { size: 14, family: 'Inter, sans-serif' },
        bodyFont: { size: 12, family: 'Inter, sans-serif' },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#ffffff',
          font: { size: 12, family: 'Inter, sans-serif' },
        },
        grid: { display: false },
      },
      y: {
        ticks: {
          color: '#ffffff',
          font: { size: 12, family: 'Inter, sans-serif' },
        },
        grid: { color: '#4B5563' },
      },
    },
  };

  if (loading) {
    return (
      <Box className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-500 p-4 sm:p-6 lg:p-8">
        <Typography variant="h4" className="text-white font-bold text-center mb-6 sm:mb-8">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-500 p-4 sm:p-6 lg:p-8">
        <Typography variant="h4" className="text-red-500 font-bold text-center mb-6 sm:mb-8">
          Error: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-500 p-4 sm:p-6 lg:p-8">
      <Typography
        variant="h4"
        className="text-white font-bold text-center mb-6 sm:mb-8"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        Module Reconciliation Dashboard
      </Typography>

      {/* Cards Section */}
      <Grid container spacing={2} className="justify-center">
        {dashboardData.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
            <Card
              className="bg-gray-800 border-none shadow-xl hover:shadow-2xl transition-shadow duration-300"
              style={{
                background: `linear-gradient(135deg, ${item.color}33, #1F2937)`,
                borderRadius: '12px',
                height: '180px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CardContent className="text-center p-4">
                <Typography
                  variant="subtitle1"
                  className="text-white font-semibold mb-2"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.9rem',
                    lineHeight: '1.2',
                    minHeight: '60px',
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="h3"
                  className="text-white font-bold"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Chart Section */}
      <Box className="mt-8 sm:mt-12 bg-gray-800 p-4 sm:p-6 rounded-xl shadow-xl">
        <Typography
          variant="h5"
          className="text-white font-bold mb-4"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Module Statistics Overview
        </Typography>
        <Box className="h-64 sm:h-80 lg:h-96">
          <Bar data={chartData} options={chartOptions} />
        </Box>
      </Box>
    </Box>
  );
};

export default Reconciliation_Dashboard;